import { useReactiveVar } from '@apollo/client';

import { useCareerLessonsListQuery } from 'core/graphql';
import { authState } from 'core/state';
import { UserRole } from 'context';

import { ICareerLessonListItem, transformCareerLessons } from './transform';

interface IUseCareerLessons {
  lessons: ICareerLessonListItem[];
  loading: boolean;
  error: string;
  refetch: () => void;
}

/**
 * The ordered career-lesson list with per-viewer lock state. Returns `[]` for
 * a non-member — the backend answers with an empty list, not an error.
 *
 * `cache-and-network` matters here beyond freshness: completing a lesson
 * unlocks the next one server-side, and the refetch after the mutation
 * (useCompleteCareerLesson) targets this operation by name.
 *
 * Skipped for non-Student roles, mirroring useMyCareerCenter: every career
 * resolver rejects a non-Student LMS token (spec 0018 §3.3), so running it
 * would only produce a guaranteed PermissionDenied before the route guard
 * bounces the visitor.
 */
export const useCareerLessons = (): IUseCareerLessons => {
  const { active_role, isDemoUser } = useReactiveVar(authState);

  const { data, loading, error, refetch } = useCareerLessonsListQuery({
    fetchPolicy: 'cache-and-network',
    skip: active_role !== UserRole.STUDENT || Boolean(isDemoUser),
  });

  return {
    lessons: transformCareerLessons(data),
    loading,
    error: error?.message || '',
    refetch,
  };
};
